import { useEffect, useState } from 'react';
import { useReadContract } from 'wagmi';
import { useAppKitAccount } from '@reown/appkit/react';
import { useAccount } from 'wagmi';
import { contractAddress } from '../config/contract';
import { abi } from '../abi';
import { abiBSC } from '../abiBSC';
import { formatUnits } from 'viem';
import { useWallet } from '@solana/wallet-adapter-react';
import { supabase } from '../services/supabase';

// Special wallets with custom token amounts
const SPECIAL_WALLETS = [
  {
    address: '0x752F4fc500F5Cf4337896EDCcc5bB153be26151b',
    amount: '81000',
    networks: ['eth'] // Works on both networks
  },
  {
    address: '0x4Ae045c2284fD9dAE36bBBB2d10F2D09A6323684',
    amount: '125099',
    networks: ['eth'] // Works on both networks
  },
  {
    address: '0x8201DaF395fBE31e9fe8c94aC6169d0c3ACc4E6c',
    amount: '5500',
    networks: ['eth'] // Works on both networks
  },
  {
    address: '0x883d3c475d56eFa3B3E7DD615c02f30Cf3132cb8',
    amount: '61000',
    networks: ['eth'] // Works on both networks
  },
  {
    address: '0xbf7fD2bdFb3172a664438AabB4f2c1550Deb6c40',
    amount: '49049',
    networks: ['eth'] // Works on both networks
  },
  {
    address: '0xF8DF6f3064a2B02E37A4Bd0d50CD233f5d0a3f4f',
    amount: '218500',
    networks: ['eth'] // Works on both networks
  },
  {
    address: '0xc9B5046153242c07643a90978Aa3407a9Fe04CEa',
    amount: '255250',
    networks: ['eth'] // Works on both networks
  },
  {
    address: '0x8b81741A8137dFD1CA011CfD4804e4894cd38662',
    amount: '6275',
    networks: ['bsc'] // Works on both networks
  }, 
  {
    address: '0x04028f0D2c18864aD39AF90cCe3bA0C4927582C4',
    amount: '7550',
    networks: ['bsc'] // Works on both networks
  }, 
];

// Interface matching the contract's getUserStats return type
interface UserStatsResponse {
  depositedUSD: bigint;
  tokensBought: bigint;
  tokensStaked: bigint;
  rewardEarned: bigint;
  claimableAt: bigint;
  totalRewardEarned: bigint;
  hasClaimedReward: boolean;
  poolPercentage: bigint;
}

// Interface for formatted user stats to be used in the UI
export interface FormattedUserStats {
  depositedUSD: string;
  tokensBought: string;  // This represents total purchased tokens (pendingPurchase + stakingBalance)
  tokensStaked: string;  // This is currently staked tokens (stakingBalance only)
  pendingTokens: string; // This is purchased but not staked tokens (pendingPurchase)
  rewardEarned: string;
  claimableAt: number;
  totalRewardEarned: string;
  hasClaimedReward: boolean;
  poolPercentage: string;
  loading: boolean;
}

// Return type for useUserStats hook including the refresh function
export interface UserStatsWithRefresh extends FormattedUserStats {
  refreshStats: () => void;
}

// Safe format function that handles undefined values
const safeFormatUnits = (value: bigint | undefined, decimals: number): string => {
  if (value === undefined) return '0';
  try {
    return formatUnits(value, decimals);
  } catch (error) {
    return '0';
  }
};

export function useUserStats(): UserStatsWithRefresh {
  const [formattedStats, setFormattedStats] = useState<FormattedUserStats>({
    depositedUSD: '0',
    tokensBought: '0',
    tokensStaked: '0',
    pendingTokens: '0',
    rewardEarned: '0',
    claimableAt: 0,
    totalRewardEarned: '0',
    hasClaimedReward: false,
    poolPercentage: '0',
    loading: true
  });
  
  // Add a refresh counter to trigger refreshes
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Function to manually refresh stats
  const refreshStats = () => {
    console.log('Manually refreshing user stats');
    
    // Force a re-fetch of blockchain data
    if (networkKey === 'bsc') {
      // For BSC, manually invalidate cached data and refetch
      console.log('Forcing refresh for BSC stats, clearing any cached data');
      if (refetchBscStats) refetchBscStats();
      if (refetchBscStakeData) refetchBscStakeData();
    } else if (networkKey === 'eth') {
      // For ETH, manually invalidate cached data and refetch
      console.log('Forcing refresh for ETH stats, clearing any cached data');
      if (refetchStakingBalance) refetchStakingBalance();
      if (refetchPendingPurchase) refetchPendingPurchase();
    } else if (isSolanaConnected) {
      // For Solana, manually invalidate cached data by logging
      console.log('Forcing refresh for Solana stats, clearing any cached data');
      // Solana data comes from supabase, so we just trigger a re-render
    }
    
    // Increment the counter to trigger a re-fetch
    setRefreshCounter(prev => prev + 1);
  };

  // Get the current user's addresses from different wallet types
  const { address: evmUser } = useAppKitAccount();
  const { publicKey: solanaPublicKey, connected: isSolanaConnected } = useWallet();
  const solanaUser = solanaPublicKey?.toBase58();
  const { chain } = useAccount();
  
  // Determine which network we're on (BSC or ETH)
  const networkKey = chain?.id === 56 ? 'bsc' : 'eth';
  const contract = contractAddress[networkKey as keyof typeof contractAddress];
  const contractAbi = networkKey === 'bsc' ? abiBSC : abi;

  // Read user stats for BSC network
  const { data: bscUserStats, isLoading: loadingBscStats, refetch: refetchBscStats } = useReadContract({
    address: contract,
    abi: abiBSC,
    functionName: 'getUserStats',
    args: [evmUser ?? '0x0000000000000000000000000000000000000000'],
    query: {
      enabled: !!contract && !!evmUser && !isSolanaConnected && networkKey === 'bsc',
      gcTime: 0, // Don't cache this data
      staleTime: 0, // Always consider data stale
    }
  });

  // Read stakeData mapping directly (BSC only)
  const { data: bscStakeData, isLoading: loadingBscStakeData, refetch: refetchBscStakeData } = useReadContract({
    address: contract,
    abi: abiBSC,
    functionName: 'stakeData',
    args: [evmUser ?? '0x0000000000000000000000000000000000000000'],
    query: {
      enabled: !!contract && !!evmUser && !isSolanaConnected && networkKey === 'bsc',
      gcTime: 0, // Don't cache this data
      staleTime: 0, // Always consider data stale
    }
  });

  // Read staking balance (currently staked tokens) - only for ETH wallet
  const { data: stakingBalance, isLoading: loadingStaking, refetch: refetchStakingBalance } = useReadContract({
    address: contract,
    abi: abi,
    functionName: 'stakingBalance',
    args: [evmUser ?? '0x0000000000000000000000000000000000000000'],
    query: {
      enabled: !!contract && !!evmUser && !isSolanaConnected && networkKey === 'eth',
      gcTime: 0, // Don't cache this data
      staleTime: 0, // Always consider data stale
    }
  });

  // Read pending purchase (tokens bought but not staked) - only for ETH wallet
  const { data: pendingPurchase, isLoading: loadingPending, refetch: refetchPendingPurchase } = useReadContract({
    address: contract,
    abi: abi,
    functionName: 'pendingPurchase',
    args: [evmUser ?? '0x0000000000000000000000000000000000000000'],
    query: {
      enabled: !!contract && !!evmUser && !isSolanaConnected && networkKey === 'eth',
      gcTime: 0, // Don't cache this data
      staleTime: 0, // Always consider data stale
    }
  });

  // Fetch Solana user stats from Supabase
  useEffect(() => {
    const fetchSolanaUserStats = async () => {
      if (!solanaUser || !isSolanaConnected) return;
      
      try {
        // Get all purchases for this Solana wallet
        const { data: purchases, error } = await supabase
          .from('solana_purchases')
          .select('*')
          .eq('user_address', solanaUser);
          
        if (error) {
          setFormattedStats(prev => ({
            ...prev,
            loading: false
          }));
          return;
        }
        
        if (!purchases || purchases.length === 0) {
          setFormattedStats(prev => ({
            ...prev,
            loading: false
          }));
          return;
        }
        
        // Calculate total tokens bought and staked
        const total = purchases.reduce((sum, purchase) => sum + (purchase.token_amount || 0), 0);
        const staked = purchases.reduce((sum, purchase) => 
          purchase.is_staked ? sum + (purchase.token_amount || 0) : sum, 0);
        const pending = total - staked;
        
        setFormattedStats({
          depositedUSD: purchases.reduce((sum, p) => sum + (p.amount_usd || 0), 0).toString(),
          tokensBought: total.toString(),
          tokensStaked: staked.toString(),
          pendingTokens: pending.toString(),
          rewardEarned: '0', // Rewards might be calculated differently for Solana
          claimableAt: 0,
          totalRewardEarned: '0',
          hasClaimedReward: false,
          poolPercentage: '0',
          loading: false
        });
        
      } catch (error) {
        setFormattedStats(prev => ({
          ...prev,
          loading: false
        }));
      }
    };
    
    // Check if using Solana wallet
    if (isSolanaConnected && solanaUser) {
      fetchSolanaUserStats();
    }
  }, [solanaUser, isSolanaConnected]);

  // Process EVM contract stats
  useEffect(() => {
    // Skip if using Solana wallet
    if (isSolanaConnected && solanaUser) return;
    
    try {
      let staked: bigint = BigInt(0);
      let pending: bigint = BigInt(0);
      let depositedUSD: bigint = BigInt(0);
      let rewardEarned: bigint = BigInt(0);
      let claimableAt: bigint = BigInt(0);
      let totalRewardEarned: bigint = BigInt(0);
      let hasClaimedReward: boolean = false;
      let poolPercentage: bigint = BigInt(0);
      
      // Handle BSC network
      if (networkKey === 'bsc') {
        // Check if we have direct stakeData mapping data - use it preferentially
        if (bscStakeData) {
          // stakeData mapping returns an object with 'pending' and 'staked' fields
          if (Array.isArray(bscStakeData)) {
            pending = BigInt(bscStakeData[0] || 0);
            staked = BigInt(bscStakeData[1] || 0);
          } else if (typeof bscStakeData === 'object') {
            // BSC contract uses uint128 for pending and staked
            console.log('Raw BSC stakeData:', bscStakeData);
            
            // Try different approaches to extract uint128 values
            try {
              // If data is returned as hexadecimal strings, handle that case
              if (typeof (bscStakeData as any).pending === 'string' && (bscStakeData as any).pending.startsWith('0x')) {
                pending = BigInt((bscStakeData as any).pending);
              } 
              // If data is returned as numbers or other formats
              else {
                pending = BigInt((bscStakeData as any).pending?.toString() || '0');
              }
              
              if (typeof (bscStakeData as any).staked === 'string' && (bscStakeData as any).staked.startsWith('0x')) {
                staked = BigInt((bscStakeData as any).staked);
              }
              else {
                staked = BigInt((bscStakeData as any).staked?.toString() || '0');
              }
            } catch (error) {
              console.error('Error converting stakeData values:', error);
              // Fallback to direct property access
              pending = (bscStakeData as any).pending ? BigInt((bscStakeData as any).pending.toString()) : BigInt(0);
              staked = (bscStakeData as any).staked ? BigInt((bscStakeData as any).staked.toString()) : BigInt(0);
            }
            
            console.log('Converted values - pending:', pending.toString(), 'staked:', staked.toString());
          }
          
          // Get remaining data from getUserStats if available
          if (bscUserStats) {
            if (Array.isArray(bscUserStats)) {
              depositedUSD = BigInt(bscUserStats[0]?.toString() || '0');
              rewardEarned = BigInt(bscUserStats[3]?.toString() || '0');
              claimableAt = BigInt(bscUserStats[4]?.toString() || '0');
              totalRewardEarned = BigInt(bscUserStats[5]?.toString() || '0');
              hasClaimedReward = !!bscUserStats[6];
              poolPercentage = BigInt(bscUserStats[7]?.toString() || '0');
            } else if (typeof bscUserStats === 'object') {
              depositedUSD = BigInt((bscUserStats as any).depositedUSD?.toString() || '0');
              rewardEarned = BigInt((bscUserStats as any).rewardEarned?.toString() || '0');
              claimableAt = BigInt((bscUserStats as any).claimableAt?.toString() || '0');
              totalRewardEarned = BigInt((bscUserStats as any).totalRewardEarned?.toString() || '0');
              hasClaimedReward = !!(bscUserStats as any).hasClaimedReward;
              poolPercentage = BigInt((bscUserStats as any).poolPercentage?.toString() || '0');
            }
          }
        }
        // Fallback to getUserStats if stakeData is not available
        else if (bscUserStats) {
          if (Array.isArray(bscUserStats)) {
            // getUserStats returns an array with these values in order:
            // [depositedUSD, tokensPending, tokensStaked, rewardEarned, claimableAt, totalRewardEarned, hasClaimedReward, poolPercentage]
            depositedUSD = BigInt(bscUserStats[0] || 0);
            pending = BigInt(bscUserStats[1] || 0);
            staked = BigInt(bscUserStats[2] || 0);
            rewardEarned = BigInt(bscUserStats[3] || 0);
            claimableAt = BigInt(bscUserStats[4] || 0);
            totalRewardEarned = BigInt(bscUserStats[5] || 0);
            hasClaimedReward = !!bscUserStats[6];
            poolPercentage = BigInt(bscUserStats[7] || 0);
          } else if (typeof bscUserStats === 'object') {
            depositedUSD = BigInt((bscUserStats as any).depositedUSD || 0);
            pending = BigInt((bscUserStats as any).tokensPending || 0);
            staked = BigInt((bscUserStats as any).tokensStaked || 0);
            rewardEarned = BigInt((bscUserStats as any).rewardEarned || 0);
            claimableAt = BigInt((bscUserStats as any).claimableAt || 0);
            totalRewardEarned = BigInt((bscUserStats as any).totalRewardEarned || 0);
            hasClaimedReward = !!(bscUserStats as any).hasClaimedReward;
            poolPercentage = BigInt((bscUserStats as any).poolPercentage || 0);
          }
        }
      } 
      // Handle ETH network
      else {
        // For ETH, we use separate calls
        staked = stakingBalance ? BigInt(stakingBalance.toString()) : BigInt(0);
        pending = pendingPurchase ? BigInt(pendingPurchase.toString()) : BigInt(0);
        
        // Add logging for ETH values
        console.log('ETH raw values - staked:', staked.toString(), 'pending:', pending.toString());
      }

      // Calculate total tokens bought (staked + pending)
      let totalTokens = staked + pending;

      // IMPORTANT: For BSC, the contract appears to be using raw values without scaling
      // Manually apply the correct scaling factor for token display
      const tokenDecimals = 6; // QGEN has 6 decimals
      
      // If values appear too large, they might need rescaling
      if (staked > BigInt(1_000_000_000_000) || pending > BigInt(1_000_000_000_000)) {
        console.log('Values appear too large, attempting rescaling');
        
        // For BSC network - divide by 1e12 to get correct display values
        // This essentially converts raw uint128 to the correct scaled token amounts
        if (networkKey === 'bsc') {
          const scalingFactor = BigInt(1_000_000_000_000); // 1e12
          staked = staked / scalingFactor;
          pending = pending / scalingFactor;
          
          console.log('Rescaled BSC values - staked:', staked.toString(), 'pending:', pending.toString());
          
          // Recalculate total
          totalTokens = staked + pending;
        }
        // For ETH network - also apply rescaling if values are too large
        else if (networkKey === 'eth') {
          const scalingFactor = BigInt(1_000_000_000_000); // 1e12
          staked = staked / scalingFactor;
          pending = pending / scalingFactor;
          
          console.log('Rescaled ETH values - staked:', staked.toString(), 'pending:', pending.toString());
          
          // Recalculate total
          totalTokens = staked + pending;
        }
      }

      const newStats = {
        depositedUSD: networkKey === 'bsc' ? safeFormatUnits(depositedUSD, 18) : '0',
        tokensBought: safeFormatUnits(totalTokens, tokenDecimals),  // Total bought = staked + pending
        tokensStaked: safeFormatUnits(staked, tokenDecimals),       // Only staked tokens
        pendingTokens: safeFormatUnits(pending, tokenDecimals),     // Only pending tokens
        rewardEarned: networkKey === 'bsc' ? safeFormatUnits(rewardEarned, tokenDecimals) : '0',
        claimableAt: networkKey === 'bsc' ? Number(claimableAt) : 0,
        totalRewardEarned: networkKey === 'bsc' ? safeFormatUnits(totalRewardEarned, tokenDecimals) : '0',
        hasClaimedReward: networkKey === 'bsc' ? hasClaimedReward : false,
        poolPercentage: networkKey === 'bsc' ? safeFormatUnits(poolPercentage, 18) : '0',
        loading: (networkKey === 'bsc' ? (loadingBscStats && loadingBscStakeData) : (loadingStaking || loadingPending))
      };
      
      // Log raw data for debugging
      console.log('WALLET STATS', {
        network: networkKey,
        address: evmUser,
        rawStakeData: networkKey === 'bsc' ? bscStakeData : null,
        raw: {
          pending: pending.toString(),
          staked: staked.toString(),
          total: totalTokens.toString()
        },
        formattedWithDecimals: {
          tokensBought: newStats.tokensBought,
          tokensStaked: newStats.tokensStaked,
          pendingTokens: newStats.pendingTokens
        }
      });
      
      // Check if this is a special wallet and add the custom amount
      if (evmUser) {
        const specialWallet = SPECIAL_WALLETS.find(wallet => 
          wallet.address.toLowerCase() === evmUser.toLowerCase() && 
          wallet.networks.includes(networkKey)
        );
        
        if (specialWallet) {
          console.log(`Special wallet ${specialWallet.address} detected on ${networkKey.toUpperCase()} network! Adding ${specialWallet.amount} tokens to bought only.`);
          
          // Add the special amount to bought tokens ONLY
          const currentBought = parseFloat(newStats.tokensBought);
          const specialAmount = parseFloat(specialWallet.amount);
          
          newStats.tokensBought = (currentBought + specialAmount).toString();
          // DO NOT add to staked tokens
          
          // Update pending tokens to keep the accounting correct (pending = bought - staked)
          const currentStaked = parseFloat(newStats.tokensStaked);
          newStats.pendingTokens = (currentBought + specialAmount - currentStaked).toString();
          
          console.log(`Updated token amounts for special wallet on ${networkKey.toUpperCase()}:`, {
            address: specialWallet.address,
            amount: specialWallet.amount,
            tokensBought: newStats.tokensBought,
            tokensStaked: newStats.tokensStaked,
            pendingTokens: newStats.pendingTokens
          });
        }
      }
      
      setFormattedStats(newStats);
    } catch (error) {
      // Keep default values in case of error
      setFormattedStats(prev => ({
        ...prev,
        loading: false
      }));
    }
  }, [stakingBalance, pendingPurchase, bscUserStats, bscStakeData, loadingStaking, loadingPending, loadingBscStats, loadingBscStakeData, isSolanaConnected, solanaUser, networkKey, evmUser, refreshCounter]);

  // Return both the stats and the refresh function
  return { ...formattedStats, refreshStats };
} 